/**
 * ControlTestRecord (round 14 A6) -- the three tests as a READER sees them.
 *
 * Once a design test's Engine proposal was accepted, the excerpt it rested
 * on appeared on no screen, and the reviewer's drawer -- which does not
 * carry the editing section -- showed no test, no excerpt and no Engine
 * line at all. So a reviewer asked to approve a status could not see that
 * the Engine had proposed it, who accepted it, or what it was based on.
 *
 * This is the record, for every seat: the result, whose it is ("the Marsad
 * Engine proposed this; <name> accepted it", or who entered it by hand),
 * what it rested on, and anything that should make a reader stop -- a
 * document that addressed the Engine, or one since withdrawn.
 */
import { useTranslation } from "react-i18next";

import { useControlTests } from "framework/api/control_test_hooks";
import { StatusPill, type PillTone } from "shared/components/StatusPill";
import type { ControlTest, ControlTestResult } from "shared/api/types";
import { formatDateTime } from "shared/time/formatDate";
import { PassageExcerpt } from "shared/documents/PassageExcerpt";

const TONE: Record<ControlTestResult, PillTone> = {
  effective: "green",
  partially_effective: "amber",
  ineffective: "red",
  not_tested: "neutral",
};

export function ControlTestRecord({
  cycleId,
  controlId,
}: {
  cycleId: string;
  controlId: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const tests = useControlTests(cycleId, controlId);
  // Read defensively: a partial payload must not take the reading down.
  const rows = Array.isArray(tests.data?.tests) ? tests.data.tests : [];
  // Round 18 A35 -- a test whose only history is a decline is still
  // record: the reviewer should know the preparer turned the Engine down.
  const shown = rows.filter(
    (x) => x.result !== "not_tested" || x.proposal || (x.declines?.length ?? 0) > 0,
  );
  if (shown.length === 0) return null;
  return (
    <div data-testid="control-review-tests">
      <p className="text-xs text-slate-500">
        {t("modules.framework.controlTesting.heading")}
      </p>
      <ul className="mt-1 space-y-2">
        {shown.map((x) => (
          <TestRecordRow key={x.id} test={x} />
        ))}
      </ul>
    </div>
  );
}

function TestRecordRow({ test }: { test: ControlTest }): JSX.Element {
  const { t, i18n } = useTranslation();
  const k = "modules.framework.controlTesting";
  const engineAccepted = Boolean(test.derived_from_run_id && test.accepted_at);
  const directives = test.directive_passages ?? [];
  const withdrawn = test.withdrawn_documents ?? [];
  const declines = Array.isArray(test.declines) ? test.declines : [];
  return (
    <li
      data-testid={`control-review-test-${test.test_type}`}
      className="text-xs text-slate-700"
    >
      <div className="flex items-center gap-2">
        <span className="font-medium">{t(`${k}.type.${test.test_type}`)}</span>
        <StatusPill tone={TONE[test.result]}>
          {t(`${k}.result.${test.result}`)}
        </StatusPill>
      </div>
      {engineAccepted ? (
        <p data-testid={`control-review-test-engine-${test.test_type}`}>
          {t(`${k}.recordEngineAccepted`, {
            who: test.accepted_by_name ?? t(`${k}.recordSomeone`),
            when: formatDateTime(test.accepted_at ?? "", i18n.language),
          })}
        </p>
      ) : (
        test.result !== "not_tested" &&
        test.tester_name && (
          <p>{t(`${k}.recordEnteredBy`, { who: test.tester_name })}</p>
        )
      )}
      {engineAccepted && test.suggested_ml != null && (
        <p className="text-slate-600">
          {t(`${k}.todSuggestedMl`, { ml: test.suggested_ml })}
        </p>
      )}
      {test.summary && (
        <p className="mt-0.5 whitespace-pre-wrap text-slate-600" dir="auto">
          {test.summary}
        </p>
      )}
      {test.evidence_excerpt && (
        <details
          className="mt-1"
          data-testid={`control-review-test-excerpt-${test.test_type}`}
        >
          <summary className="cursor-pointer text-slate-600">
            {t(`${k}.todExcerptSummary`, {
              count: test.covering_documents?.length ?? 0,
            })}
          </summary>
          <PassageExcerpt
            text={test.evidence_excerpt}
            passages={directives.map((d) => d.passage)}
            testId={`control-review-test-excerpt-text-${test.test_type}`}
          />
        </details>
      )}
      {/* Round 18 A35 -- the reviewer read a count and not the passage. */}
      {directives.length > 0 && (
        <details
          data-testid={`control-review-test-directives-${test.test_type}`}
          className="mt-1 rounded border border-rag-red/40 bg-rag-red/5 px-2 py-1 text-[11px] text-rag-red"
        >
          <summary className="cursor-pointer">
            {t(`${k}.todDirectiveHeading`, { count: directives.length })}{" "}
            <span className="underline">{t(`${k}.recordDirectivesShow`)}</span>
          </summary>
          <ul className="mt-1 space-y-1">
            {directives.map((d, i) => (
              <li key={i}>
                <span className="text-slate-600" dir="auto">
                  <bdi>{d.document}</bdi>:{" "}
                </span>
                <q dir="auto">{d.passage}</q>
                {d.hidden && (
                  <span className="ms-1 text-[10px] font-semibold text-amber-900">
                    ({t("documents.reading.notVisible")})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
      {withdrawn.length > 0 && (
        <p className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
          {t(`${k}.resultRestsOnWithdrawn`, {
            count: withdrawn.length,
            names: withdrawn.join(", "),
          })}
        </p>
      )}
      {declines.length > 0 && (
        <ul
          data-testid={`control-review-test-declines-${test.test_type}`}
          className="mt-1 space-y-0.5 text-[11px] text-slate-600"
        >
          {declines.map((d, i) => {
            const who = d.declined_by_name ?? t(`${k}.recordSomeone`);
            const when = formatDateTime(d.declined_at, i18n.language);
            return (
              <li key={i}>
                {d.declined_result
                  ? t(`${k}.recordDeclined`, {
                      who,
                      when,
                      result: t(`${k}.result.${d.declined_result}`),
                    })
                  : t(`${k}.recordDeclinedNoResult`, { who, when })}
              </li>
            );
          })}
        </ul>
      )}
      {test.proposal && (
        <p
          data-testid={`control-review-test-pending-${test.test_type}`}
          className="mt-1 text-[11px] text-slate-500"
        >
          {t(`${k}.recordProposalWaiting`)}
        </p>
      )}
    </li>
  );
}
